import { useEffect, useState } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

/**
 * Banner discreto no topo que aparece quando o usuário perde conexão.
 *
 * Comportamento:
 *  - Aparece imediatamente quando `online: false`
 *  - Quando volta online, mostra brevemente "Voltou!" e some
 *  - Botão "Tentar agora" recarrega a página (útil quando o user já
 *    voltou pra rede mas o app não detectou — Safari iOS às vezes
 *    atrasa o evento online)
 *
 * Não bloqueia uso do app — só sinaliza. O app já tem fallback de cache
 * (publicCache) pra muitas telas, então offline ≠ app quebrado.
 */
export function OfflineBanner() {
  const { online } = useNetworkStatus();
  const [showRecovered, setShowRecovered] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!online) {
      setWasOffline(true);
      setShowRecovered(false);
      return;
    }
    if (wasOffline) {
      // Acabou de voltar. Mostra "Voltou" por 2s.
      setShowRecovered(true);
      const t = window.setTimeout(() => {
        setShowRecovered(false);
        setWasOffline(false);
      }, 2000);
      return () => window.clearTimeout(t);
    }
  }, [online, wasOffline]);

  const visible = !online || showRecovered;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          className="fixed inset-x-0 top-0 z-[60] flex justify-center pointer-events-none"
          style={{ paddingTop: 'var(--safe-top, 0px)' }}
        >
          <div
            className={`mt-2 pointer-events-auto inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold shadow-lg backdrop-blur-md transition-colors ${
              online
                ? 'border-emerald-400/30 bg-emerald-500/15 text-emerald-200'
                : 'border-amber-400/30 bg-amber-500/15 text-amber-100'
            }`}
          >
            {/* role=status com aria-live só na MENSAGEM. Antes englobava o
                botão, o que é anti-padrão (status não deve conter
                interativos). */}
            <span role="status" aria-live="polite" className="inline-flex items-center gap-2">
              {online ? (
                <>
                  <RefreshCw size={12} aria-hidden="true" />
                  <span>Conectado de novo</span>
                </>
              ) : (
                <>
                  <WifiOff size={12} aria-hidden="true" />
                  <span>Sem net — mostrando o que dá</span>
                </>
              )}
            </span>
            {!online && (
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="ml-1 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-50 hover:bg-white/25 transition-colors"
              >
                Recarregar
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
